import { conversationStudy } from '../context';
import { recistGrid, recistSummaryLine } from './logic';
import { sendLabel, sendToReport } from './send';
import type { RecistTable } from './shapes';
import { button, el, loading, type RadiologistDeps, textBlock, toolCard, toolFailure } from './ui';

/**
 * "RECIST" (contract v3 §2.7): ris builds the RECIST 1.1 table from the patient's tracked lesions
 * (up to five targets with a long axis, one column per study date) and the response. No model. The
 * summary line ris writes can go to the draft's Hallazgos section through §2.1.
 */

export const RECIST_TEXT = {
  tool: 'RECIST',
  title: 'Tabla RECIST 1.1',
  loading: 'calculando…',
  lesion: 'Lesión diana',
  sum: 'Suma de diámetros',
  incomplete: 'incompleto',
  none: 'No hay lesiones diana con eje largo en seguimiento. Sígalas con "Lesiones" para calcular RECIST.',
  forReport: 'Texto para el informe:',
  failed: 'No fue posible calcular la tabla RECIST.',
} as const;

export function renderRecistTable(table: RecistTable): HTMLTableElement {
  const grid = recistGrid(table);
  const out = el('table', 'assistant-recist-table');
  const head = el('tr', '');
  head.appendChild(el('th', '', RECIST_TEXT.lesion));
  grid.columns.forEach((c) => {
    const th = el('th', c.roles.includes('actual') ? 'recist-current' : '');
    th.appendChild(el('div', 'recist-date', c.date));
    const marks = [...c.roles, ...(c.complete ? [] : [RECIST_TEXT.incomplete])];
    if (marks.length > 0) th.appendChild(el('div', 'recist-roles', marks.join(' · ')));
    head.appendChild(th);
  });
  out.appendChild(el('thead', '')).appendChild(head);
  const body = out.appendChild(el('tbody', ''));
  grid.rows.forEach((row) => {
    const tr = body.appendChild(el('tr', ''));
    tr.appendChild(el('th', 'recist-label', row.label));
    row.cells.forEach((cell) => tr.appendChild(el('td', '', cell)));
  });
  const sum = out.appendChild(el('tfoot', '')).appendChild(el('tr', 'recist-sum'));
  sum.appendChild(el('th', '', RECIST_TEXT.sum));
  grid.sums.forEach((cell) => sum.appendChild(el('td', '', cell)));
  return out;
}

function render(deps: RadiologistDeps, study: string, table: RecistTable): HTMLElement[] {
  if (table.targets.length === 0 || table.timepoints.length === 0) return [el('p', 'assistant-tool-note', RECIST_TEXT.none)];
  const wrap = el('div', 'assistant-recist-wrap');
  wrap.appendChild(renderRecistTable(table));
  const out: HTMLElement[] = [wrap, el('div', `assistant-recist-response response-${table.response}`, recistSummaryLine(table))];
  const text = table.text;
  if (text) {
    out.push(textBlock(RECIST_TEXT.forReport, text));
    const row = el('div', 'assistant-finding-actions');
    row.appendChild(button(sendLabel(), 'assistant-recist-send', () => void sendToReport(deps.shell, deps.api, [{ section: 'findings', text }], study)));
    out.push(row);
  }
  return out;
}

export async function showRecist(deps: RadiologistDeps): Promise<void> {
  const study = conversationStudy(deps.shell);
  const { body } = toolCard(deps, 'recist', RECIST_TEXT.title, study);
  loading(body, RECIST_TEXT.loading);
  try {
    body.replaceChildren(...render(deps, study, await deps.api.recist(study)));
  } catch (error) {
    console.warn('[viewer] recist failed', error);
    body.replaceChildren(el('p', 'assistant-tool-note assistant-tool-error', toolFailure(error, 'recist', RECIST_TEXT.failed)));
  }
}
